/* =============================================================================
   ics-UI SHELL ENTRY - THE JOB STATUS FAMILY.
   Bundle entry -> dist-v2/ics-jobstatus.min.js, loaded by the page shell
   Views/Shared/_Meta.cshtml after ics-combo.min.js (and by _MobileLayout
   through the same tag). It is NOT the full library: core +
   Registry + the jobstatus module (components/ics-jobstatus.js) + the facade
   bridge + auto-mount, and it claims nothing else.

   WHAT IT DOES ON A PAGE
   1. Mounts the own bar `[data-ics="jobstatus"]` at ready time, again after any
      markup injection (MutationObserver in facade/mount.js) and after a
      page-level `Ics.parse(ctx)`. The page's job-status overlay (the
      shell's `#icsprogressbarParent` / `#icsprogressbar`, authored with the own
      attribute) is one of those mounts; it is ONE per page, realm-shared.
   2. `$.fn.progressbar` becomes a DISPATCHER: an own receiver (mounted Jobstatus,
      or `[data-ics="jobstatus"]` markup) goes to the component's bridge; every
      other receiver goes to the implementation that held the name before us,
      untouched; mixed sets split; foreign statics carried.
   3. Claims the whole job-status PAGE surface unconditionally (last write wins,
      re-asserted in the ready queue): `$.icsProgressBar(isStart[, {message,
      percent}])` (the payload is honoured), ProgressBarShow / ProgressBarHide /
      ProgressBarSetValue / UpdateProgressBar (either argument order) /
      UpdatePickerProgress / StartPickerProgress / StopPickerProgress /
      InitializeProgressBar / StopSignalr (the SignalR progress feed lives inside
      the component - one reused connection) - AND the busy-action names
      `IcsBusyAction` / `IcsButtonBusy` / `IcsButtonIdle` / `IcsButtonIsBusy`,
      which are aliases of `Ics.jobstatus.action / busy / idle / isBusy` (the
      full list is JOBSTATUS_GLOBALS). The busy action belongs to THIS module -
      one module owns a job: its status and the control that fired it - never to
      the button bundle.
   4. `$.fn.icsJobstatus` is ours; publishes `Ics.components.Jobstatus` +
      `Ics.jobstatus` ({begin, update, end, show, hide, bar, section, action,
      busy, idle, isBusy, settings, ...}) ADDITIVELY onto an existing `Ics`, or a
      minimal `Ics` when none exists.
   RULES: a view never authors its own splash markup or spinner - it calls
   `$.icsProgressBar(true/false)` or `Ics.jobstatus.begin()`; a per-element wait
   is `Ics.jobstatus.section(host[, message])`. The ramp is an ANIMATION, never a
   measurement: a real percent stops it.
   CSS: the shell links lib-v2/css/components/ics-jobstatus.css raw.
   ============================================================================= */
import { Component } from './core/component.js';
import { Registry } from './services/registry.js';
import { makeBridge, registerAllFacades } from './facade/register.js';
import { mount, unmount, installAutoMount, installMutationMount } from './facade/mount.js';
import { Jobstatus, jobstatus, attachJobstatusFamily, attachJobstatusGlobals, JOBSTATUS_GLOBALS, JOBSTATUS_SETTINGS } from './components/ics-jobstatus.js';

const OWN = '[data-ics="jobstatus"]';

/* own-form markup only, no alias claim - the plain name is taken by the dispatcher */
{ const e = Registry.get('Jobstatus'); if (e) e.claimName = null; }

export function isOurs(el) {
  if (!el || el.nodeType !== 1) return false;
  const inst = Component.get(el);
  if (inst) return inst instanceof Jobstatus;
  return !!(el.matches && el.matches(OWN));
}

/** one page-level dispatcher over whatever holds the plain name; idempotent */
export function installJobstatusDispatch($) {
  if (!$ || !$.fn) return false;
  const cur = $.fn.progressbar;
  if (typeof cur === 'function' && cur._icsJobstatusDispatch) return true;
  const foreign = typeof cur === 'function' ? cur : null;
  const ours = makeBridge($, 'Jobstatus', Jobstatus, OWN);
  const dispatch = function () {
    if (this.length === 0 || !foreign) return ours.apply(this, arguments);
    const a = [], b = [];
    for (let i = 0; i < this.length; i++) (isOurs(this[i]) ? a : b).push(this[i]);
    if (!b.length) return ours.apply(this, arguments);
    if (!a.length) return foreign.apply(this, arguments);
    const ra = ours.apply($(a), arguments);
    const rb = foreign.apply($(b), arguments);
    const r = isOurs(this[0]) ? ra : rb;
    return (r && r.jquery) ? this : r;
  };
  if (foreign) Object.assign(dispatch, foreign);          // defaults / methods stay readable
  dispatch._icsJobstatusDispatch = true;
  dispatch._icsForeign = foreign;
  dispatch._icsBridge = ours;
  $.fn.progressbar = dispatch;
  return true;
}


const g = globalThis;
if (typeof document !== 'undefined') {
  const $ = g.jQuery || g.$;
  if ($ && $.fn) {
    registerAllFacades($);                                   // $.fn.icsJobstatus: ours
    installJobstatusDispatch($);
    attachJobstatusFamily($);                                // $.icsProgressBar: ours (payload honoured)
    attachJobstatusGlobals(g);                               // the page globals: ours
    $(() => {                                                // ready-queue re-assert (the shell-entry contract)
      registerAllFacades($);
      installJobstatusDispatch($);
      attachJobstatusFamily($);
      attachJobstatusGlobals(g);
    });
  } else {
    attachJobstatusGlobals(g);
  }
  installAutoMount();
  installMutationMount();
}

const IcsJobstatusEntry = {
  version: '2.2.0-jobstatus',
  Component,
  registry: Registry,
  components: { Jobstatus },
  jobstatus,
  JOBSTATUS_GLOBALS, JOBSTATUS_SETTINGS,
  mount, unmount,
};
if (!g.Ics) g.Ics = IcsJobstatusEntry;
else {
  g.Ics.components = Object.assign(g.Ics.components || {}, { Jobstatus });
  g.Ics.jobstatus = jobstatus;
  if (!g.Ics.JOBSTATUS_SETTINGS) g.Ics.JOBSTATUS_SETTINGS = JOBSTATUS_SETTINGS;
  if (!g.Ics.JOBSTATUS_GLOBALS) g.Ics.JOBSTATUS_GLOBALS = JOBSTATUS_GLOBALS;   // the page globals' identity, for gates
}
export default IcsJobstatusEntry;
